import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Clock,
  Layers,
  Hash,
  Zap,
  ArrowLeft,
} from 'lucide-react';
import CaptionTool from '@/components/aitools/CaptionTool';
import ScheduleTool from '@/components/aitools/ScheduleTool';
import BulkCaptionTool from '@/components/aitools/BulkCaptionTool';

type ToolKey = 'caption' | 'bulk' | 'schedule';

interface ToolDef {
  key: ToolKey;
  name: string;
  tagline: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}

const TOOLS: ToolDef[] = [
  {
    key: 'caption',
    name: 'Caption Studio',
    tagline: 'Single post',
    description:
      'Generate three caption variants (Cinematic · Conversational · Editorial) and a 30-tag hashtag set from a theme and optional image.',
    icon: Sparkles,
    accent: 'from-[#1A3B5C] to-[#0D1B2A]',
  },
  {
    key: 'bulk',
    name: 'Bulk Generator',
    tagline: 'Multi-post batch',
    description:
      'Paste a week of post themes and generate captions + hashtags for all of them at once. Export to CSV.',
    icon: Layers,
    accent: 'from-[#8B6F47] to-[#C9A961]',
  },
  {
    key: 'schedule',
    name: 'Auto-Schedule',
    tagline: 'Optimal post times',
    description:
      'AI recommends the best post time for each row based on post type and theme. Side-by-side comparison with current times.',
    icon: Clock,
    accent: 'from-emerald-700 to-emerald-500',
  },
];

const AIToolsDashboard: React.FC = () => {
  const [active, setActive] = useState<ToolKey>('caption');
  const ActiveTool = TOOLS.find((t) => t.key === active)!;
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0D1B2A] border-b border-[#C9A961]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="hidden sm:flex items-center gap-2 font-sans text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-white/15 text-white/60 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors mr-2"
              title="Back to Admin Dashboard"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Admin
            </button>
            <div className="w-10 h-10 rounded bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#C9A961]" />
            </div>
            <div>
              <h1 className="font-serif text-xl text-white font-light">
                AI Social Tools
                <span className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase ml-3">
                  Backend Dashboard
                </span>
              </h1>
              <p className="font-sans text-[10px] text-white/50 tracking-wider mt-0.5">
                Caption generation · Bulk processing · Optimal post-time suggestions — works with any
                content
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-white/15 text-white/60 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
            >
              View Site
            </button>
            <span className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-[#C9A961]/40 text-[#C9A961] inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> v1.0
            </span>
          </div>
        </div>
      </header>



      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Tool selector cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`text-left p-5 border transition-all ${
                  isActive
                    ? 'border-[#1A3B5C] bg-white shadow-md scale-[1.01]'
                    : 'border-slate-200 bg-white hover:border-[#C9A961] hover:shadow-sm'
                }`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className={`w-10 h-10 flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${t.accent} text-white`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-sans text-[9px] tracking-[0.25em] uppercase text-[#8B6F47]">
                      {t.tagline}
                    </div>
                    <h3 className="font-serif text-lg text-[#1A3B5C] leading-tight">{t.name}</h3>
                  </div>
                  {isActive && (
                    <span className="px-2 py-0.5 bg-[#1A3B5C] text-white font-sans text-[9px] tracking-wider uppercase rounded">
                      Active
                    </span>
                  )}
                </div>
                <p className="font-sans text-xs text-slate-600 leading-relaxed">{t.description}</p>
              </button>
            );
          })}
        </div>

        {/* Active tool banner */}
        <div className="bg-gradient-to-r from-[#0D1B2A] to-[#1A3B5C] p-5 mb-6 flex items-center gap-4 border-l-4 border-[#C9A961]">
          <div className="w-12 h-12 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center flex-shrink-0">
            <ActiveTool.icon className="w-6 h-6 text-[#C9A961]" />
          </div>
          <div>
            <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961]">
              Currently using
            </div>
            <h2 className="font-serif text-2xl text-white font-light">{ActiveTool.name}</h2>
          </div>
          <div className="ml-auto hidden md:flex items-center gap-2 text-white/40 font-sans text-[10px] tracking-wider uppercase">
            <Hash className="w-3.5 h-3.5" /> Powered by AI Gateway
          </div>
        </div>

        {/* Active tool */}
        {active === 'caption' && <CaptionTool />}
        {active === 'bulk' && <BulkCaptionTool />}
        {active === 'schedule' && <ScheduleTool />}
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-6 mt-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between flex-wrap gap-2">
          <p className="font-sans text-[10px] tracking-wider uppercase text-slate-400">
            AI Social Tools · Standalone Dashboard
          </p>
          <p className="font-sans text-[10px] tracking-wider uppercase text-slate-400">
            All processing routed through the AI Gateway · No data stored
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AIToolsDashboard;
